// Visit schedule: a week at a time, with scheduling and changes.
import { api } from "../lib/api";
import { VISIT_TYPES } from "../lib/catalog";
import { fmt, t } from "../lib/i18n";
import { Link } from "../lib/router";
import { h, invalidate, local, resource } from "../lib/ui";
import { act, Async, Button, Dialog, Field, formData, HealthChip, isBusy, openDialog, Select, VisitStatus } from "../shared/components";
import { PageHead } from "./app";

const pad = (n: number) => String(n).padStart(2, "0");
function localInput(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function isoDay(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function scheduleVisitDialog(home: any | null, onDone: () => void) {
  const refs = resource("admin.refs", () => api.get("/api/admin/refs"));
  const homes = home ? null : resource("admin.home.list..", () => api.get("/api/admin/homes"));
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(10, 0, 0, 0);
  openDialog((close) => {
    const experts = (refs.data?.experts ?? []).map((e: any) => ({ value: e.id, label: e.name }));
    return (
      <Dialog title={t("admin.visits.scheduleTitle")} onClose={close}>
        <form class="stack" onSubmit={(e: Event) => {
          const d = formData(e);
          act("visit.create", async () => {
            const res = await api.post("/api/admin/visits", {
              home_id: home?.id ?? d.home_id, type: d.type, scheduled_for: new Date(d.when).toISOString(),
              duration_minutes: Number(d.duration), expert_id: d.expert_id, second_expert_id: d.second_expert_id,
            });
            close();
            invalidate("admin.visits");
            onDone();
            if (res.warnSecondExpert) openDialog((c2) => (
              <Dialog title={t("admin.visits.secondExpertTitle")} onClose={c2} footer={<Button onClick={c2}>{t("common.ok")}</Button>}>
                <p>{t("admin.visits.secondExpertBody")}</p>
              </Dialog>
            ));
          }, t("admin.visits.scheduled"));
        }}>
          {!home ? (
            <Field label={t("fields.home")} id="v-home">
              <Select id="v-home" name="home_id" options={(homes?.data ?? []).filter((x: any) => x.status !== "terminated").map((x: any) => ({ value: x.id, label: `${x.code} · ${x.address_line1}` }))} />
            </Field>
          ) : <p class="strong">{home.address_line1} <span class="muted small">{home.code}</span></p>}
          <div class="cols-2">
            <Field label={t("fields.visit_type")} id="v-type"><Select id="v-type" name="type" value="periodic" options={VISIT_TYPES.map((x) => ({ value: x, label: t(`visitType.${x}`) }))} /></Field>
            <Field label={t("admin.visits.duration")} id="v-dur">
              <div class="input-affix"><input class="input tabular" id="v-dur" name="duration" inputMode="numeric" value="60" /><span class="affix">{t("admin.settings.units.minutes")}</span></div>
            </Field>
          </div>
          <Field label={t("admin.visits.when")} id="v-when"><input class="input" id="v-when" name="when" type="datetime-local" required value={localInput(tomorrow)} /></Field>
          <div class="cols-2">
            <Field label={t("fields.expert_id")} id="v-ex"><Select id="v-ex" name="expert_id" value={home?.primary_expert_id ?? experts[0]?.value ?? ""} options={experts} /></Field>
            <Field label={t("fields.second_expert_id")} id="v-ex2" hint={t("admin.visits.secondHint")}><Select id="v-ex2" name="second_expert_id" value="" options={[{ value: "", label: t("common.none") }, ...experts]} /></Field>
          </div>
          <div class="row end">
            <Button kind="secondary" onClick={close}>{t("common.cancel")}</Button>
            <Button type="submit" busy={isBusy("visit.create")}>{t("admin.visits.schedule")}</Button>
          </div>
        </form>
      </Dialog>
    );
  });
}

function emergencyDialog(v: any, onDone: () => void) {
  openDialog((close) => (
    <Dialog title={t("admin.visits.emergencyEntry")} onClose={close}>
      <form class="stack" onSubmit={(e: Event) => {
        const d = formData(e);
        act("visit.emergency", async () => {
          await api.post(`/api/admin/visits/${v.id}/emergency-entry`, { note: d.note });
          close();
          onDone();
        }, t("admin.visits.emergencyApproved"));
      }}>
        <p class="small">{t("admin.visits.emergencyEntryBody")}</p>
        <Field label={t("admin.visits.emergencyEntryPrompt")} id="em-note"><textarea class="textarea" id="em-note" name="note" required /></Field>
        <div class="row end">
          <Button kind="secondary" onClick={close}>{t("common.cancel")}</Button>
          <Button kind="danger" type="submit" busy={isBusy("visit.emergency")}>{t("admin.visits.emergencyEntry")}</Button>
        </div>
      </form>
    </Dialog>
  ));
}

function editVisitDialog(v: any, onDone: () => void) {
  const refs = resource("admin.refs", () => api.get("/api/admin/refs"));
  const locked = ["in_progress", "completed"].includes(v.status);
  openDialog((close) => {
    const experts = (refs.data?.experts ?? []).map((e: any) => ({ value: e.id, label: e.name }));
    return (
      <Dialog title={`${t(`visitType.${v.type}`)} · ${v.address_line1}`} onClose={close}>
        {locked ? (
          <div class="stack">
            <p>{t("admin.visits.locked")}</p>
            <div class="row end"><Button onClick={close}>{t("common.done")}</Button></div>
          </div>
        ) : (
          <form class="stack" onSubmit={(e: Event) => {
            const d = formData(e);
            act("visit.save", async () => {
              await api.patch(`/api/admin/visits/${v.id}`, {
                scheduled_for: new Date(d.when).toISOString(), duration_minutes: Number(d.duration), expert_id: d.expert_id,
                second_expert_id: d.second_expert_id, ...(d.status !== v.status ? { status: d.status } : {}),
              });
              close();
              onDone();
            }, t("common.saved"));
          }}>
            <Field label={t("admin.visits.when")} id="ev-when" hint={v.status === "entry_approved" ? t("admin.visits.moveResetsApproval") : undefined}>
              <input class="input" id="ev-when" name="when" type="datetime-local" required value={localInput(new Date(v.scheduled_for))} />
            </Field>
            <div class="cols-2">
              <Field label={t("admin.visits.duration")} id="ev-dur"><input class="input tabular" id="ev-dur" name="duration" inputMode="numeric" value={String(v.duration_minutes)} /></Field>
              <Field label={t("fields.status")} id="ev-st">
                <Select id="ev-st" name="status" value={v.status}
                  options={Array.from(new Set([v.status, "scheduled", "notified", "time_confirmed", "cancelled"])).map((x) => ({ value: x, label: t(`visitStatus.${x}`) }))} />
              </Field>
            </div>
            <div class="cols-2">
              <Field label={t("fields.expert_id")} id="ev-ex"><Select id="ev-ex" name="expert_id" value={v.expert_id ?? ""} options={experts} /></Field>
              <Field label={t("fields.second_expert_id")} id="ev-ex2"><Select id="ev-ex2" name="second_expert_id" value={v.second_expert_id ?? ""} options={[{ value: "", label: t("common.none") }, ...experts]} /></Field>
            </div>
            {v.type === "incident" && v.status !== "entry_approved" ? (
              <div class="note warn stack tight">
                <span>{t("admin.visits.emergencyEntryBody")}</span>
                <div><Button kind="danger-outline" size="small" onClick={() => emergencyDialog(v, () => { close(); onDone(); })}>{t("admin.visits.emergencyEntry")}</Button></div>
              </div>
            ) : null}
            <div class="row end">
              <Button kind="secondary" onClick={close}>{t("common.cancel")}</Button>
              <Button type="submit" busy={isBusy("visit.save")}>{t("common.save")}</Button>
            </div>
          </form>
        )}
      </Dialog>
    );
  });
}

export function VisitsPage() {
  const s = local("screen:visits", () => {
    const d = new Date();
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // Monday
    return { from: isoDay(d) };
  });
  const r = resource(`admin.visits.${s.from}`, () => api.get(`/api/admin/visits?from=${s.from}&days=7`));
  const shift = (days: number) => {
    const d = new Date(s.from + "T12:00:00");
    d.setDate(d.getDate() + days);
    s.set({ from: isoDay(d) });
  };
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(s.from + "T12:00:00");
    d.setDate(d.getDate() + i);
    return isoDay(d);
  });
  const today = isoDay(new Date());
  return (
    <div class="stack loose">
      <PageHead title={t("admin.visits.title")} intro={t("admin.visits.intro")}
        actions={[
          <div class="row" style="gap:6px">
            <Button kind="secondary" size="small" icon="back" onClick={() => shift(-7)}>{t("admin.visits.prev")}</Button>
            <Button kind="secondary" size="small" onClick={() => shift(7)}>{t("admin.visits.next")}</Button>
          </div>,
          <Button icon="calendar" onClick={() => scheduleVisitDialog(null, () => r.reload())}>{t("admin.visits.schedule")}</Button>,
        ]} />
      <p class="strong">{t("admin.visits.weekOf", { from: fmt.date(days[0], "short"), to: fmt.date(days[6], "medium") })}</p>
      {Async(r, (rows: any[]) => (
        <div class="stack">
          {days.map((day) => {
            const list = rows.filter((v) => v.day === day);
            return (
              <section class="panel" key={day}>
                <div class="panel-head" style="padding:12px 20px">
                  <h3 style={day === today ? "color:var(--c-primary)" : ""}>{fmt.date(day, "weekday")}{day === today ? ` · ${t("common.today")}` : ""}</h3>
                  <span class="small muted">{t("admin.visits.count", { count: list.length })}</span>
                </div>
                {list.length ? (
                  <table class="table"><tbody>
                    {list.map((v) => (
                      <tr class="link" onClick={() => editVisitDialog(v, () => r.reload())}>
                        <td class="tabular strong nowrap" style="width:110px">{fmt.time(v.scheduled_for)}</td>
                        <td>
                          <Link href={`/admin/homes/${v.home_id}`} class="strong">{v.address_line1}</Link>
                          <span class="sub">{v.home_code} · {t(`visitType.${v.type}`)} · {v.duration_minutes} min</span>
                        </td>
                        <td class="small">{v.expert ?? "—"}{v.second_expert ? <span class="sub">+ {v.second_expert}</span> : null}</td>
                        <td class="right">
                          {v.access_revoked_at ? <span class="chip critical">{t("admin.homes.accessRevoked")}</span> : v.outcome ? <HealthChip health={v.outcome} /> : <VisitStatus status={v.status} />}
                        </td>
                      </tr>
                    ))}
                  </tbody></table>
                ) : null}
              </section>
            );
          })}
        </div>
      ), 6)}
    </div>
  );
}
